import { useEffect, useMemo, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import './ExperienceReward.css';

const SAMPLES = 60;

function rewardTimeline(amount, locale) {
  const values = Array.from({ length: SAMPLES + 1 }, (_, step) =>
    step === SAMPLES ? amount : Math.floor(amount * (1 - (1 - step / SAMPLES) ** 2)));
  const formatted = amount.toLocaleString(locale);
  const digitCount = String(amount).length;
  const padded = values.map(value => String(value).padStart(digitCount, '\u00a0'));
  let position = 0;
  const columns = Array.from(formatted, character => {
    if (!/\d/.test(character)) return { separator: character, minimum: 10 ** (digitCount - position) };
    const digits = [];
    const rows = padded.map(value => {
      const digit = value[position];
      if (digits.at(-1) !== digit) digits.push(digit);
      return digits.length - 1;
    });
    position++;
    return { digits: digits.join('\n'), rows };
  });
  // Sample the timeline instead of allocating one reel row per awarded point.
  return { values, columns, formatted };
}

export default function ExperienceReward({ amount, unit, caption, locale, className = '', icon: Icon = Sparkles, onComplete }) {
  const reducedMotion = useReducedMotion();
  const timeline = useMemo(() => rewardTimeline(Math.max(0, Math.floor(amount)), locale), [amount, locale]);
  const [sample, setSample] = useState(0);
  const completeCallback = useRef(onComplete);
  useEffect(() => { completeCallback.current = onComplete; }, [onComplete]);

  useEffect(() => {
    let frame;
    const start = performance.now();
    const tick = now => {
      const progress = reducedMotion || amount <= 0 ? 1 : Math.max(0, Math.min((now - start - 220) / 1550, 1));
      setSample(Math.floor(progress * SAMPLES));
      if (progress < 1) frame = requestAnimationFrame(tick);
      else completeCallback.current?.();
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [amount, reducedMotion]);

  const value = timeline.values[sample];
  const complete = sample === SAMPLES;
  return <div className={`experience-reward ${className}`} data-complete={complete} role="img" aria-label={`+${timeline.formatted}${unit ? ` ${unit}` : ''}${caption ? ` · ${caption}` : ''}`}>
    <span className="experience-reward-icon" aria-hidden="true">
      <svg className="experience-reward-ring" viewBox="0 0 72 72" fill="none">
        <circle className="experience-reward-ring-track" cx="36" cy="36" r="33" />
        <circle className="experience-reward-ring-fill" cx="36" cy="36" r="33" pathLength="1" strokeDasharray="1" strokeDashoffset={amount > 0 ? 1 - value / amount : 0} />
      </svg>
      <Icon className="experience-reward-symbol" />
    </span>
    <strong className="experience-reward-score" aria-hidden="true"><span className="experience-reward-plus">+</span><span className="experience-reward-count" data-final={timeline.formatted}>
      <span className="experience-reward-value">{value.toLocaleString(locale)}</span>
      <span className="experience-reward-reels">
        {timeline.columns.map((column, index) => column.separator
          ? <span key={index} className="experience-reward-separator" style={{ visibility: value >= column.minimum ? 'visible' : 'hidden' }}>{column.separator}</span>
          : <span key={index} className="experience-reward-reel" data-digits={column.digits} style={{ '--digit-row': column.rows[sample] }} />)}
      </span>
      {value > 0 && !complete && <span key={value} className="experience-reward-tick" />}
    </span>{unit && <> <span className="experience-reward-unit">{unit}</span></>}</strong>
    {caption && <span className="experience-reward-caption" aria-hidden="true">{caption}</span>}
    <span className="experience-reward-particles" aria-hidden="true"><i /><i /><i /><i /><i /><i /></span>
  </div>;
}
